import Input from '@/components/Input';
import ModalV2 from '@/components/ModalV2';
import PanelAlert from '@/components/panel/PanelAlert';
import { BiCode, FiShield } from '@/components/react-icons/main';
import Spinner from '@/components/Spinner';
import Toggle from '@/components/Toggle';
import ToggleButton from '@/components/ToggleButton';
import FloatingSelectV2 from '@/components/v2/FloatingSelectV2';
import IconV2 from '@/components/v2/IconV2';
import LabeledCheckbox from '@/components/v3/LabeledCheckbox';
import Stack from '@/components/v3/Stack';
import { confirmDialog } from '@/lib/confirmDialog.js';
import { getCredentialById, searchCredentials } from '@/redux/actions/gravityActions';
import selectors from '@/redux/selectors/selectors';
import appActions from '@/screens/apps/redux/actions/appActions';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors';
import CredentialStatusIcon from '@/screens/credentials/components/CredentialStatusIcon';
import { randomId } from '@shared/utils/randomId.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const DESCRIPTION_PLACEHOLDER = 'Provide a formal description of this credential: identify the owner and the scope of use. For credentials not under a MindCloud account, state how the owner has communicated and documented authorization for MindCloud to use this connection.';
const AGENT_CONNECTION_CONFIRM_TITLE = 'Change Agent Connection';
const AGENT_CONNECTION_CONFIRM_MESSAGE = 'This will replace the current agent connection.\n\nThe agent will use a different credential for testing and runs, and the existing description and attestations will be removed.\n\nYou will need to select a new connection and complete the attestations again.\n\nAre you sure you want to continue?';
const tabOptions = [
  { value: 'connection', label: 'Connection' },
  { value: 'settings', label: 'Settings' }
];

const settingsRows = [
  {
    key: 'blockCreates',
    label: 'Block Creates',
    description: 'Prevent external agents from running actions marked as Create.'
  },
  {
    key: 'blockUpdates',
    label: 'Block Updates',
    description: 'Prevent external agents from running actions marked as Update.'
  },
  {
    key: 'blockDeletes',
    label: 'Block Deletes',
    description: 'Prevent external agents from running actions marked as Delete.'
  }
];

const getCredentialLabel = (cred) => {
  const credName = cred?.secrets?.label || cred?.meta?.label || 'Unnamed';
  const companyName = cred?.companyName;
  if (companyName && !credName.startsWith(companyName)) {
    return `${companyName} - ${credName}`;
  }
  return credName;
};

const AgentConnectionModal = ({ show, onHide, initialCredentialId, initialCompanyId, initialDescription, initialBlockCreates, initialBlockUpdates, initialBlockDeletes }) => {
  const appId = useSelector((state) => appScreenSelectors.activeApp(state)?.id);
  const agentConnection = useSelector((state) => appScreenSelectors.activeApp(state)?.agentConnection);
  const allCredentials = useSelector(selectors.credentials);
  const isAdmin = useSelector(selectors.admin);
  const userCompanyId = useSelector(selectors.companyId);
  const effectiveCompanyId = isAdmin ? 'MINDCLOUD' : userCompanyId;

  const [activeTab, setActiveTab] = useState('connection');
  const [selectedCredentialId, setSelectedCredentialId] = useState(initialCredentialId || null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(initialCompanyId || null);
  const [description, setDescription] = useState(initialDescription || '');
  const [blockCreates, setBlockCreates] = useState(initialBlockCreates === true);
  const [blockUpdates, setBlockUpdates] = useState(initialBlockUpdates === true);
  const [blockDeletes, setBlockDeletes] = useState(initialBlockDeletes === true);
  const [attestSandbox, setAttestSandbox] = useState(false);
  const [attestPermission, setAttestPermission] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  const triggerClass = useMemo(() => `agent-connection-credential-select-${randomId()}`, []);

  const allCredentialsForApp = useMemo(() => {
    if (!appId || !effectiveCompanyId || !allCredentials) {
      return [];
    }
    return allCredentials.filter((cred) => cred?.appId === appId && cred?.companyId === effectiveCompanyId);
  }, [appId, effectiveCompanyId, allCredentials]);

  const selectedCredential = useMemo(() => {
    if (!selectedCredentialId) {
      return null;
    }
    const fromList = allCredentials?.find((c) => c.credentialId === selectedCredentialId);
    if (fromList) {
      return fromList;
    }
    return searchResults.find((c) => c.credentialId === selectedCredentialId) || null;
  }, [allCredentials, selectedCredentialId, searchResults]);

  const credentialsForSelect = useMemo(() => {
    if (searchTerm && searchResults.length > 0) {
      const combined = [...allCredentialsForApp];
      const seen = new Set(combined.map((c) => c.credentialId));
      searchResults.forEach((cred) => {
        if (!seen.has(cred.credentialId)) {
          seen.add(cred.credentialId);
          combined.push(cred);
        }
      });
      return combined;
    }
    const base = [...allCredentialsForApp];
    if (agentConnection?.credentialId && !base.some((c) => c.credentialId === agentConnection.credentialId)) {
      const existing = allCredentials?.find((c) => c.credentialId === agentConnection.credentialId);
      if (existing) {
        base.unshift(existing);
      }
    }
    return base;
  }, [allCredentialsForApp, searchTerm, searchResults, agentConnection?.credentialId, allCredentials]);

  const credentialOptions = useMemo(() => {
    return credentialsForSelect.map((cred) => ({
      label: getCredentialLabel(cred),
      value: cred.credentialId,
      credential: cred,
      icon: cred?.companyId === 'MINDCLOUD' ? FiShield : BiCode
    }));
  }, [credentialsForSelect]);

  const selectedCredentialTitle = useMemo(() => {
    if (!selectedCredential) {
      return 'No connection';
    }

    return selectedCredential.companyId === 'MINDCLOUD' ? 'MindCloud' : 'External Company';
  }, [selectedCredential]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearch && appId) {
      setIsSearchLoading(true);
      searchCredentials({ search: debouncedSearch, appId }, (results) => {
        setSearchResults(results || []);
        setIsSearchLoading(false);
      });
    } else {
      setSearchResults([]);
      setIsSearchLoading(false);
    }
  }, [debouncedSearch, appId]);

  useEffect(() => {
    if (show) {
      setActiveTab('connection');
      setSelectedCredentialId(initialCredentialId || null);
      setSelectedCompanyId(initialCompanyId || null);
      setDescription(initialDescription || '');
      setBlockCreates(initialBlockCreates === true);
      setBlockUpdates(initialBlockUpdates === true);
      setBlockDeletes(initialBlockDeletes === true);
      setAttestSandbox(false);
      setAttestPermission(false);
    }
  }, [show, initialCredentialId, initialCompanyId, initialDescription, initialBlockCreates, initialBlockUpdates, initialBlockDeletes]);

  const handleCredentialSelect = useCallback(
    (option) => {
      if (option === null || option?.value === null) {
        setSelectedCredentialId(null);
        setSelectedCompanyId(null);
        return;
      }

      if (option && typeof option === 'object' && option.credential) {
        setSelectedCredentialId(option.credential.credentialId);
        setSelectedCompanyId(option.credential.companyId);
      } else if (option && (typeof option === 'string' || typeof option === 'number')) {
        const cred = credentialsForSelect.find((c) => c.credentialId === option);
        if (cred) {
          setSelectedCredentialId(cred.credentialId);
          setSelectedCompanyId(cred.companyId);
        }
      }
    },
    [credentialsForSelect]
  );

  const handleSave = useCallback(async () => {
    if (!selectedCredentialId) {
      appActions.set('agentConnection', null);
      onHide();
      return;
    }

    if (!selectedCompanyId || !description.trim() || !attestSandbox || !attestPermission) {
      return;
    }

    const isReplacingConnection = Boolean(agentConnection?.credentialId) && selectedCredentialId !== agentConnection.credentialId;
    if (isReplacingConnection) {
      const confirmed = await confirmDialog({
        title: AGENT_CONNECTION_CONFIRM_TITLE,
        message: AGENT_CONNECTION_CONFIRM_MESSAGE,
        confirm: 'Continue',
        cancel: 'Cancel'
      });
      if (!confirmed) {
        return;
      }
    }

    appActions.set('agentConnection', {
      credentialId: selectedCredentialId,
      companyId: selectedCompanyId,
      description: description.trim(),
      blockCreates,
      blockUpdates,
      blockDeletes
    });
    if (selectedCompanyId) {
      getCredentialById(selectedCredentialId, { companyId: selectedCompanyId });
    }
    onHide();
  }, [selectedCredentialId, selectedCompanyId, description, attestSandbox, attestPermission, blockCreates, blockUpdates, blockDeletes, agentConnection?.credentialId, onHide]);

  const canSave = !selectedCredentialId || (Boolean(selectedCompanyId) && description.trim().length > 0 && attestSandbox && attestPermission);

  const actions = useMemo(
    () => [
      { children: 'Cancel', onClick: onHide },
      { children: 'Save', onClick: handleSave, className: 'primary', disabled: !canSave }
    ],
    [onHide, handleSave, canSave]
  );

  if (!show) {
    return null;
  }

  return (
    <ModalV2 //
      show={show}
      onHide={onHide}
      title="Agent Connection"
      maxWidth="520px"
      className="agent-connection-modal"
      actions={actions}
      portal
      centered>
      <Stack gap="md" direction="vertical">
        <div className="agent-connection-modal-tabs">
          <ToggleButton options={tabOptions} value={activeTab} onChange={setActiveTab} size="sm" fullWidth={false} />
        </div>

        {activeTab === 'connection' && (
          <>
            <PanelAlert variant="warn" description={'This connection grants an AI agent full access to the account, including the ability to create, read, update, and delete any data or resources.\n\nOnly connect accounts that are:\n• Owned by MindCloud, or\n• A client-approved sandbox or test environment.\n\nTreat this account as disposable. Data may be modified or permanently deleted.'} />

            <div className="agent-connection-modal-connection">
              <div className="comp-input">
                <div className="label">Connection</div>
                <div className={`credential-select-trigger ${triggerClass}`} data-title={selectedCredentialTitle}>
                  <IconV2 className="select-icon" icon={selectedCredential?.companyId === 'MINDCLOUD' ? FiShield : BiCode} />
                  <input readOnly placeholder="Select connection..." value={selectedCredential ? getCredentialLabel(selectedCredential) : ''} className="credential-select-input" />
                  {selectedCredential && <CredentialStatusIcon credential={selectedCredential} className="dark" />}
                </div>
                {isSearchLoading && <Spinner />}
                <FloatingSelectV2 triggers={[`.${triggerClass}`]} options={credentialOptions} value={selectedCredentialId} onChange={handleCredentialSelect} useSearch={true} onSearch={setSearchTerm} loading={isSearchLoading} placeholder="Search connections..." nullable={true} nullableLabel="None" />
              </div>
            </div>

            <Input //
              label="Description"
              type="textarea"
              placeholder={DESCRIPTION_PLACEHOLDER}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />

            <div className="attestations">
              <LabeledCheckbox label="I confirm this connection points only to a sandbox or test environment that can be modified or deleted without consequence." checked={attestSandbox} onChange={setAttestSandbox} />
              <LabeledCheckbox label="I confirm the account owner has explicitly authorized MindCloud to use this connection for AI agent testing and operations." checked={attestPermission} onChange={setAttestPermission} />
            </div>
          </>
        )}

        {activeTab === 'settings' && (
          <div className="agent-connection-settings">
            <PanelAlert variant="info" description="Configure which mutating operations external agents are blocked from running with this app connection." />

            {settingsRows.map((row) => {
              const isActive = row.key === 'blockCreates' ? blockCreates : row.key === 'blockUpdates' ? blockUpdates : blockDeletes;
              const onChange = row.key === 'blockCreates' ? setBlockCreates : row.key === 'blockUpdates' ? setBlockUpdates : setBlockDeletes;

              return (
                <div key={row.key} className="settings-row">
                  <div className="settings-row-text">
                    <div className="settings-row-label">{row.label}</div>
                    <div className="settings-row-description">{row.description}</div>
                  </div>
                  <Toggle size="sm" active={isActive} onChange={onChange} />
                </div>
              );
            })}
          </div>
        )}
      </Stack>
    </ModalV2>
  );
};

export default AgentConnectionModal;
